<template>
  <div>
    <b-modal
      centered
      no-close-on-backdrop
      title="Share Existing Documents"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="shareDocuments"
      v-model="showExistingDocuments"
      header-class="bg-purple"
      dialog-class="modal-attach-doc"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Close
        </b-button>
        <b-button
          :disabled="!isShareButtonActive"
          variant="primary"
          @click="ok()"
        >
          Share
        </b-button>
      </template>
      <div class="more-info mb-2">
        <span class="b-inline">Select documents to share:</span>
        <span class="b-inline pull-right"
          >{{ selectedDocuments.length }} documents selected
          <a
            class="btn-link"
            @click="cleanSelectedDocuments"
            style="cursor: pointer"
            >Clear</a
          ></span
        >
      </div>
      <b-form-input
        v-model="docSearch"
        placeholder="Search by Ref# or Document type..."
        @input="fetchDocsByEOC(docSearch, 0)"
      ></b-form-input>
      <document-picker
        ref="documentPicker"
        :docs="documentsByEoc"
        :showToggle="true"
        v-model="selectedDocuments"
        :isLastPage="attachDocPagination.lastPage"
        @loadMore="
          fetchDocsByEOC(docSearch, attachDocPagination.page + 1, true)
        "
      />
      <b-form-group label="Share with:">
        <b-form-checkbox-group v-model="shareWith" stacked name="share-with">
          <b-form-checkbox
            v-for="(access, index) in portalAccesses"
            :key="index"
            :value="access.id"
          >
            {{ access.name }} ({{ access.relationship }})
          </b-form-checkbox>
        </b-form-checkbox-group>

        <b-alert v-if="portalAccesses.length == 0" show variant="warning"
          >There's no granted users</b-alert
        >
      </b-form-group>
    </b-modal>
    <b-modal
      v-model="editorShown"
      title="Document Requests & Sharing"
      size="md-lg"
      content-class="document-sharing-modal"
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
      body-class="p-4"
      no-close-on-backdrop
      centered
      scrollable
      hide-footer
      modal-class="doc-share-modal"
    >
      <div class="document-sharing-container">
        <b-tabs>
          <b-tab title-item-class="share-tab-item" title="Requested Documents">
            <div class="d-flex justify-content-center p-3 bg-extra-light-gray">
              <b-button variant="success" @click="showSharePackageModal = true">
                <i class="fas fa-folder mr-1"></i> Request Document Package
              </b-button>
            </div>
            <div class="mt-4">
              <div class="d-flex justify-content-between align-items-center">
                <div style="font-size: 18px;">Requested documents</div>
                <b-button variant="primary" @click="showShareFormsModal = true">
                  Request new documents
                </b-button>
              </div>
              <div class="table-wrap mt-3">
                <b-table
                  :items="requestedDocuments"
                  :fields="requestedDocumentFields"
                  sticky-header
                  borderless
                >
                  <template #cell(actions)="row">
                    <b-link @click="stopSharing(row.item)" class="text-danger">
                      Cancel request
                    </b-link>
                  </template>
                  <template #cell(form.name)="data">
                    {{
                      data.item.form
                        ? data.item.form.name
                        : data.item.formGroup.name
                    }}
                  </template>
                </b-table>
              </div>
            </div>
            <div style="margin-top: 38px;">
              <div class="d-flex justify-content-between align-items-center">
                <div style="font-size: 18px;">Recurrent documents</div>
                <b-button
                  variant="primary"
                  @click="showShareFormRecurrentModal = true"
                >
                  New recurrent documents
                </b-button>
              </div>
              <div class="table-wrap mt-3">
                <b-table
                  :items="sharedRecurrentDocuments"
                  :fields="sharedRecurrentDocumentFields"
                  sticky-header
                  borderless
                >
                  <template #cell(actions)="row">
                    <b-link
                      @click="stopSharingRecurrent(row.item)"
                      class="text-danger"
                    >
                      Remove
                    </b-link>
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab>
          <b-tab title-item-class="share-tab-item" title="Shared Documents">
            <div class="mt-2">
              <div class="d-flex flex-row justify-content-between">
                <div style="font-size: 18px;">Shared Documents</div>
                <b-button variant="primary" @click="showExistingDoc"
                  >Share existing documents</b-button
                >
              </div>
              <div class="table-wrap mt-3">
                <b-table
                  :items="sharedDocuments"
                  :fields="sharedDocumentFields"
                  sticky-header
                  borderless
                >
                  <template #cell(actions)="row">
                    <b-link
                      v-if="
                        hasCurrentUserDocumentPermission(
                          row.item.form.id,
                          'isReadOnly'
                        )
                      "
                      :disabled="!row.item.document && !row.item.documentGroup"
                      @click="viewDocument(row.item)"
                      class="mr-2"
                    >
                      View
                    </b-link>
                    <b-link @click="stopSharing(row.item)" class="text-danger">
                      Remove Access
                    </b-link>
                  </template>
                  <template #cell(status)="data">
                    <span
                      v-if="data.item.document"
                      :class="
                        `d-block badge-v2 py-2 badge-${getStatusClass(
                          data.item.document.status
                        )} badge-fixed-size`
                      "
                    >
                      {{ getStatusText(data.item.document.status) }}
                    </span>
                    <span
                      v-else-if="data.item.documentGroup"
                      :class="
                        `d-block badge-v2 py-2 badge-${getStatusClass(
                          data.item.documentGroup.status
                        )} badge-fixed-size`
                      "
                    >
                      {{ getStatusText(data.item.documentGroup.status) }}
                    </span>
                  </template>
                  <template #cell(form.name)="data">
                    {{
                      data.item.form
                        ? data.item.form.name
                        : data.item.formGroup.name
                    }}
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>

    <share-package-modal
      v-if="showSharePackageModal"
      v-model="showSharePackageModal"
      :patientId="patientId"
      :eocId="eocId"
      @onModify="onModifySharePackage"
    />
    <share-form-recurrent-modal
      v-if="showShareFormRecurrentModal"
      v-model="showShareFormRecurrentModal"
      :patientId="patientId"
      :eocId="eocId"
      @onModify="onModifyShareFormRecurrent"
    />
    <share-multiple-forms-modal
      v-if="showShareFormsModal"
      v-model="showShareFormsModal"
      :patientId="patientId"
      :eocId="eocId"
      :categories="['main', 'releaseOfInformation']"
      @onModify="onModifyShareForms"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import portalDocumentServer from "../../../server/portalDocument";
import portalAccessServer from "../../../server/portalAccess";
import SharePackageModal from "./SharePackageModal.vue";
import ShareFormRecurrentModal from "./ShareFormRecurrentModal.vue";
import ShareMultipleFormsModal from "./ShareMultipleFormsModal.vue";
import DocumentPicker from "../../controls/DocumentPicker";
import documentServer from "../../../server/document";
import documentGroupServer from "../../../server/documentGroup";

export default {
  name: "DocumentSharingModal",
  components: {
    SharePackageModal,
    ShareFormRecurrentModal,
    ShareMultipleFormsModal,
    DocumentPicker
  },
  props: ["eocId", "patientId"],
  data() {
    return {
      requestedDocuments: [],
      sharedDocuments: [],
      docSearch: null,
      selectedDocuments: [],
      documentsByEoc: [],
      sharedRecurrentDocuments: [],
      showSharePackageModal: false,
      showShareFormsModal: false,
      showShareFormRecurrentModal: false,
      showExistingDocuments: false,
      clientSearchChanged: false,
      lastTimeout: null,
      attachDocPagination: {
        pageSize: 10,
        page: 0,
        count: 0,
        lastPage: false
      },
      shareWith: [],
      portalAccesses: [],
      show: true
    };
  },
  async mounted() {
    await this.fetchDocsByEOC("", 0);
    await this.fetchSharedDocuments();
    await this.fetchSharedRecurrentForms();
    this.portalAccesses = await portalAccessServer.findAllAccess(
      this.patientId
    );
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    showExistingDoc() {
      this.showExistingDocuments = true;
    },
    async shareDocuments() {
      await portalDocumentServer.shareDocuments({
        portalAccessIds: this.shareWith,
        documents: this.selectedDocuments,
        episodeOfCareId: this.eocId
      });
      this.fetchSharedDocuments();
      this.showAlertSuccess();
    },
    async fetchDocsByEOC(search, page, stackResult) {
      this.clientSearchChanged = true;
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          this.attachDocPagination.page = 0;
          const optionsResult = await documentServer.queryImproved(
            {
              searchPhrase: search,
              episodeOfCareId: [this.eocId],
              formId: [],
              status: [],
              fromDate: null,
              toDate: null,
              effectiveFromDate: null,
              effectiveToDate: null,
              createdBy: [],
              page: page,
              pageSize: this.attachDocPagination.pageSize,
              orderBy: "creationDate",
              orderByDescending: false,
              category: [],
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : []
            },
            {
              "Hide-Loading": true
            }
          );
          this.attachDocPagination.count = optionsResult.count
            ? optionsResult.count
            : 0;
          this.attachDocPagination.page = page ? page : 0;
          this.isLastPage();
          if (stackResult) {
            let newValues = optionsResult.documents.map(doc => ({
              value: doc.id,
              label: `Ref# ${doc.documentRefNumber} | ${doc.form.name}`,
              data: doc
            }));
            this.documentsByEoc = this.documentsByEoc.concat(newValues);
          } else {
            this.documentsByEoc = optionsResult.documents.map(doc => ({
              value: doc.id,
              label: `Ref# ${doc.documentRefNumber} | ${doc.form.name}`,
              data: doc
            }));
          }
          this.documentsByEoc = this.documentsByEoc.filter(d =>
            this.hasCurrentUserDocumentPermission(d.data.form.id, "isReadOnly")
          );
          this.clientSearchChanged = false;
        } else {
          this.clientSearchChanged = false;
          this.clientOptions = [];
        }
      }, 1000);
    },
    isLastPage() {
      let totalPages = Math.ceil(
        this.attachDocPagination.count / this.attachDocPagination.pageSize
      );
      let result = totalPages - 1 == this.attachDocPagination.page;
      this.attachDocPagination.lastPage = result;
    },
    async fetchSharedDocuments() {
      const sharedDocuments = await portalDocumentServer.findAllSharedDocuments(
        this.eocId
      );
      sharedDocuments.sort((a, b) => {
        return a.sharedAt.localeCompare(b.sharedAt);
      });
      this.sharedDocuments = sharedDocuments.filter(d => d.readOnly);
      this.requestedDocuments = sharedDocuments.filter(d => !d.readOnly);
    },
    async fetchSharedRecurrentForms() {
      const recurrentForms = await portalDocumentServer.findAllSharedRecurrentForms(
        this.eocId
      );
      recurrentForms.sort((a, b) => {
        return a.sharedAt.localeCompare(b.sharedAt);
      });
      this.sharedRecurrentDocuments = recurrentForms;
    },
    viewDocument(data) {
      if (data.document) {
        this.$emit("viewDocument", data.document.id);
      } else if (data.documentGroup) {
        const url = documentGroupServer.viewUrl(data.documentGroup.id);
        window.open(url, "_blank");
      }
    },
    cleanSelectedDocuments() {
      this.selectedDocuments = [];
      this.$refs["documentPicker"].clear();
    },
    async stopSharing(data) {
      if (this.show) {
        this.show = false;
        this.$dialog
          .alert(
            {
              title: "Are you sure you want to stop sharing this document?"
            },
            {
              view: "confirm-delete"
            }
          )
          .then(async () => {
            await portalDocumentServer.stopSharingDocument(data.id);
            this.showAlertSuccess();
            this.fetchSharedDocuments();
            this.show = true;
          })
          .catch(() => {
            this.show = true;
          });
      }
    },
    async stopSharingRecurrent(data) {
      this.$dialog
        .alert(
          {
            title: "Are you sure you want to stop sharing this document?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await portalDocumentServer.stopSharingRecurrentForm(data.id);
          this.showAlertSuccess();
          this.fetchSharedRecurrentForms();
        });
    },
    formatFrequency(item) {
      let result = `Repeat every ${item.frequencyNumber} ${item.frequencyUnit}`;

      if (item.frequencyUnit == "Weeks") {
        result +=
          " on " +
          [
            ...(item.sunday ? ["Sunday"] : []),
            ...(item.monday ? ["Monday"] : []),
            ...(item.tuesday ? ["Tuesday"] : []),
            ...(item.wednesday ? ["Wednesday"] : []),
            ...(item.thursday ? ["Thursday"] : []),
            ...(item.friday ? ["Friday"] : []),
            ...(item.saturday ? ["Saturday"] : [])
          ].join(", ");
      }

      return result;
    },
    getDaysIntervalId(item) {
      const { intervalAmount, interval } = item;
      switch (`${intervalAmount}${interval}`) {
        case "2Days":
          return "every2Days";
        case "3Days":
          return "every3Days";
        case "1Weeks":
          return "1timePerWeek";
        case "2Weeks":
          return "everyTwoWeeks";
        case "3Weeks":
          return "every3Weeks";
        case "28Days":
          return "every28Days";
        case "30Days":
          return "every30Days";
        default:
          return null;
      }
    },
    onModifySharePackage() {
      this.showSharePackageModal = false;
      this.fetchSharedDocuments();
      this.fetchSharedRecurrentForms();
    },
    onModifyShareForms() {
      this.showShareFormsModal = false;
      this.fetchSharedDocuments();
    },
    onModifyShareFormRecurrent() {
      this.showShareFormRecurrentModal = false;
      this.fetchSharedDocuments();
      this.fetchSharedRecurrentForms();
    },
    getStatusClass(status) {
      let className = "warning";
      if (status == "Completed") {
        className = "success";
      }
      if (status == "Draft") {
        className = "danger";
      }
      return className;
    },
    getStatusText(status) {
      let statusText = "Pending";
      if (status == "Completed") {
        statusText = "Completed";
      }
      if (status == "Draft") {
        statusText = "Draft";
      }
      return statusText;
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    requestedDocumentFields() {
      return [
        { key: "form.name", label: "Document", sortable: true },
        { key: "sharedTo", label: "Portal Accounts", sortable: true },
        {
          key: "sharedAt",
          label: "Requested on",
          sortable: true,
          formatter: value => this.formatDateOnly(value)
        },
        { key: "sharedBy.fullName", label: "Requested by", sortable: true },
        { key: "actions", label: "", class: "action-cell" }
      ];
    },
    sharedDocumentFields() {
      return [
        { key: "document.documentRefNumber", label: "REF#", sortable: true },
        { key: "form.name", label: "Document", sortable: true },
        { key: "status", label: "Status" },
        { key: "sharedTo", label: "Portal Accounts", sortable: true },
        {
          key: "sharedAt",
          label: "Shared/Requested on",
          sortable: true,
          formatter: value => this.formatDateOnly(value)
        },
        {
          key: "submittedDate",
          label: "Shared date",
          sortable: true,
          formatter: value => this.formatDateOnly(value)
        },
        { key: "actions", label: "", class: "action-cell" }
      ];
    },
    sharedRecurrentDocumentFields() {
      return [
        { key: "form.name", label: "Document", sortable: true },
        {
          key: "frequency",
          label: "Frequency",
          sortable: false,
          formatter: (value, key, item) => this.formatFrequency(item)
        },
        { key: "sharedTo", label: "Portal Accounts", sortable: true },
        { key: "actions", label: "", class: "action-cell" }
      ];
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    isShareButtonActive() {
      if (this.selectedDocuments.length > 0 && this.shareWith.length > 0) {
        return true;
      }
      return false;
    }
  }
};
</script>

<style scoped>
.table-wrap {
  white-space: nowrap;
}
.table-wrap >>> thead tr th {
  background-color: #f5f8fc !important;
}

.table-wrap >>> .action-cell {
  text-align: right;
  width: 200px;
}
.table-wrap {
  font-size: 13px;
}
.table-wrap >>> th {
  font-weight: 100;
}
.b-table-sticky-header {
  height: 100%;
  max-height: calc(100vh - 28rem);
}
.badge-fixed-size {
  max-width: 101px;
}
.document-sharing-container >>> .share-tab-item {
  font-size: 13px;
}

@media (max-width: 992px) {
  /deep/ .doc-share-modal .modal-dialog {
    max-width: 800px !important;
  }
}
.document-sharing-container >>> .nav-tabs {
  background-color: #f5f8fc;
  border-bottom: none;
  padding: 0 39px;
  margin: -23px -23px 0px;
}
.document-sharing-container >>> .nav-tabs .nav-link {
  color: #777;
  font-weight: bold;
  background: none;
  border: none;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-radius: 2px;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  margin-right: 10px;
}
.document-sharing-container >>> .nav-tabs .nav-link.active {
  color: #4044e6;
  border-bottom-color: #4044e6;
}
.document-sharing-container >>> .tab-content {
  padding: 1rem;
  height: calc(100vh - 315px);
  overflow-y: auto;
  overflow-x: hidden;
}
@supports (position: sticky) {
  ::v-deep .b-table-sticky-header > .table.b-table > thead > tr > th {
    position: sticky !important;
  }
}
</style>
